import React, { Fragment, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import FancyDropdown, {
  FancyDropdownMenu,
  FancyDropdownMenuItem
} from 'dpl/components/FancyDropdown';
import Icon from 'dpl/common/components/Icon';
import FixedScrollWrapper, {
  OPT_OUT_CLASS
} from 'dpl/components/FixedScrollWrapper';

export default function SimpleMultiSelect({
  availableTags,
  groupedTags,
  label,
  onAllValuesClear,
  onValueRemove,
  onValueSelect,
  placeholder,
  selectedValues
}) {
  const [isOpen, setIsOpen] = useState(false);

  const [groupedOptions, availableOptions] = useMemo(() => {
    return [
      groupedTags || [[null, availableTags]],
      availableTags || groupedTags.reduce((c, [, opts]) => [...c, ...opts], [])
    ];
  }, [availableTags, groupedTags]);

  const selectedTags = useMemo(() => {
    return availableOptions.filter(tag => selectedValues.includes(tag.value));
  }, [availableOptions, selectedValues]);

  function handleTagToggle(tag) {
    if (selectedValues.includes(tag.value)) {
      onValueRemove(tag.value);
    } else {
      onValueSelect(tag.value);
    }
  }

  const hasSelectedValues = selectedValues.length > 0;

  return (
    <div className={classnames(`MultiSelect`, OPT_OUT_CLASS)}>
      <FixedScrollWrapper>
        <div className="mb3 flex justify-between">
          <label className="f2 fw-medium">{label}</label>
          {selectedValues.length > 0 && (
            <button
              className="f2 underline mid-gray"
              onClick={onAllValuesClear}
              type="button"
            >
              Clear
            </button>
          )}
        </div>

        <FancyDropdown
          isOpen={isOpen}
          closeOnItemClick={false}
          onChange={setIsOpen}
        >
          <button
            className={classnames(
              'MultiSelect__trigger w-100 ph3 mb1 ba b--light-gray br3 flex justify-between align-items-center bg-transparent black',
              {
                pv1: hasSelectedValues,
                pv2: !hasSelectedValues
              }
            )}
            data-test-id="MultiSelect__trigger"
            onClick={() => setIsOpen(prevIsOpen => !prevIsOpen)}
            type="button"
          >
            <span>
              {selectedTags.length > 0 ? (
                <span>
                  {selectedTags.map((tag, idx) => (
                    <span
                      className="pv1 ph2 br2 mr1 mv1 dib bg-near-white f2"
                      key={idx}
                    >
                      {tag.title}
                    </span>
                  ))}
                </span>
              ) : (
                <span className="db black">{placeholder}</span>
              )}
            </span>
            <span>
              <Icon name={isOpen ? 'fat-arrow-up' : 'fat-arrow-down'} />
            </span>
          </button>

          {/* todo a11y: aria-haspopup=listbox, role=listbox */}
          <FancyDropdownMenu
            className="overflow-y-auto ba b--silver br3 break-word"
            contentClassName=""
          >
            {groupedOptions.map(([groupTitle, tags], idx) => (
              <Fragment key={idx}>
                {tags.length > 0 && groupTitle && (
                  <p className="pv3 ph3 b--light-gray bb fw-medium">
                    {groupTitle}
                  </p>
                )}
                {tags.map(tag => {
                  const isSelected = selectedValues.includes(tag.value);

                  return (
                    <FancyDropdownMenuItem
                      className="ph0"
                      key={tag.value}
                      itemKey={tag.value}
                    >
                      <button
                        className={classnames(
                          'w-100 pv2 ph4 flex justify-between align-items-center',
                          {
                            'bb b--light-gray':
                              idx < availableOptions.length - 1
                          }
                        )}
                        data-test-id={`${tag.title}-${tag.value}-${
                          isSelected ? 'selected' : 'unselected'
                        }`}
                        onClick={() => handleTagToggle(tag)}
                        type="button"
                      >
                        <span>{tag.title}</span>
                        <span
                          className={classnames('ph1 pv1 br-100', {
                            'bg-white black': !isSelected,
                            'bg-black white': isSelected
                          })}
                          data-selected={isSelected}
                        >
                          <Icon name="checkmark" width="16px" height="16px" />
                        </span>
                      </button>
                    </FancyDropdownMenuItem>
                  );
                })}
              </Fragment>
            ))}
          </FancyDropdownMenu>
        </FancyDropdown>
      </FixedScrollWrapper>
    </div>
  );
}

SimpleMultiSelect.propTypes = {
  // [{ title: 'Foo', value: 'foo }]
  availableTags: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
        .isRequired
    })
  ),
  // ['Standard', [{ title: 'Blue Merle', value: 7]]
  groupedTags: PropTypes.arrayOf(PropTypes.array),
  label: PropTypes.node.isRequired,
  onAllValuesClear: PropTypes.func.isRequired,
  onValueRemove: PropTypes.func.isRequired,
  onValueSelect: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  selectedValues: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  ).isRequired
};

SimpleMultiSelect.defaultProps = {
  availableTags: null,
  groupedTags: null,
  placeholder: 'All'
};
